import type { Task } from 'types/interfaces';

export type EmployeeAssignmentRulesParams = {
  departmentId?: number;
  start?: string;
  end?: string;
  intervalStart?: string;
  intervalEnd?: string;  
  effort?: number;
  taskTypeId?: number;
  attainments?: any;
};

const toValidIsoString = (date: Date): string | null =>
  isNaN(date.getTime()) ? null : date.toISOString();

const buildLocalDate = (
  year: number,
  month: number,
  day: number,
  hours: number,
  minutes: number
): Date | null => {
  const date = new Date(year, month - 1, day, hours, minutes);
  if (
    date.getFullYear() !== year ||
    date.getMonth() !== month - 1 ||
    date.getDate() !== day ||
    date.getHours() !== hours ||
    date.getMinutes() !== minutes
  ) {
    return null;
  }
  return date;
};

const parseDottedDateTime = (value: string): Date | null => {
  const match = value.match(
    /^(\d{2})\.(\d{2})\.(\d{4})(?:\s+(\d{1,2}):(\d{2}))?$/
  );
  if (!match) return null;

  const first = Number(match[1]);
  const second = Number(match[2]);
  const year = Number(match[3]);
  const hours = match[4] === undefined ? 0 : Number(match[4]);
  const minutes = match[5] === undefined ? 0 : Number(match[5]);

  if (
    first < 1 ||
    second < 1 ||
    hours < 0 ||
    hours > 23 ||
    minutes < 0 ||
    minutes > 59
  ) {
    return null;
  }

  const monthFirstDate =
    first <= 12 ? buildLocalDate(year, first, second, hours, minutes) : null;
  const dayFirstDate =
    second <= 12 ? buildLocalDate(year, second, first, hours, minutes) : null;

  return monthFirstDate ?? dayFirstDate;
};

export const toEmployeeAssignmentDate = (
  value?: string | number | Date | null
): string | null => {
  if (value === null || value === undefined || value === '') return null;

  if (value instanceof Date) {
    return toValidIsoString(value);
  }

  if (typeof value === 'number') {
    return toValidIsoString(new Date(value));
  }

  const trimmed = value.trim();
  if (!trimmed) return null;

  if (/^\d+$/.test(trimmed)) {
    return toEmployeeAssignmentDate(Number(trimmed));
  }

  const dottedDate = parseDottedDateTime(trimmed);
  if (dottedDate) return dottedDate.toISOString();

  const parsed = new Date(trimmed);
  return toValidIsoString(parsed);
};

const normalizeEffort = (effort?: number | string | null): number | null => {
  if (effort === null || effort === undefined || effort === '') return null;
  const normalized = Number(effort);
  return Number.isFinite(normalized) && normalized > 0 ? normalized : null;
};

export const buildEmployeeAssignmentRulesParams = ({
  departmentId,
  plannedStartDate,
  plannedEndDate,
  intervalStart,
  intervalEnd,
  effort,
  taskTypeId,
  attainments
}: {
  departmentId?: number | null;
  plannedStartDate?: string | number | Date | null;
  plannedEndDate?: string | number | Date | null;
  intervalStart?: string | null;
  intervalEnd?: string | null;  
  effort?: number | string | null;
  taskTypeId?: number | null;
  attainments?: any
}): EmployeeAssignmentRulesParams => {
  const start = toEmployeeAssignmentDate(plannedStartDate);
  const end = toEmployeeAssignmentDate(plannedEndDate);
  const normalizedEffort = normalizeEffort(effort);
  const result: EmployeeAssignmentRulesParams = {};
  if (departmentId && !isNaN(departmentId) && departmentId > 0) {
    result.departmentId = departmentId;
  }
  if (start) result.start = start;
  if (end) result.end = end;
  if (normalizedEffort) result.effort = normalizedEffort;
  if (taskTypeId) result.taskTypeId = taskTypeId;
  if (attainments && attainments.length > 0) {
    result.attainments = attainments;
  }
  if (intervalStart) {
    result.intervalStart = intervalStart;
  } else if (start) {
    result.start = start;
  }
  
  if (intervalEnd) {
    result.intervalEnd = intervalEnd;
  } else if (end) {
    result.end = end;
  }
  return result;
};

export const buildEmployeeAssignmentRulesParamsFromTask = (
  task: Task,
  departmentId?: number | null
): EmployeeAssignmentRulesParams => {
  const attainmentIds = task.attainments?.map((item: any) => item?.id).filter((id: number) => id !== undefined) || [];
  return buildEmployeeAssignmentRulesParams({
    departmentId,
    plannedStartDate: task.plannedStartDate || (task as any).planStart,
    plannedEndDate: task.plannedEndDate || (task as any).planFinish,
    effort: task.effort,
    taskTypeId: task.taskTypeId,
    attainments: attainmentIds,   
  });
};
